import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import * as DependenciesV2Store from '../../../../redux/dependenciesV2';
import { Badge, BadgeColors, BadgeShapes } from '../../../generic/Badge';
import List from '../../../generic/List';
import PaginationRangeWithControl from '../../../generic/Pagination/PaginationRangeWithControl';
import PartialResultRefreshButton from '../../generic/PartialResult/PartialResultRefreshButton';
import useFetchDependencyAction from '../hooks/useFetchDependencyAction';
import type { ManuallyAddedDependencyRequestKind } from '../../../../../typings/api';
import type { DependencyV2Modals } from '../hooks/useDependencyModals';
import type { DependencyToRemove } from '../modals/RemoveDependency';

import DependencyTableContents from './DependencyTableContents';

import styles from '../dependencies.module.less';

type Props = {
  modals: DependencyV2Modals;
  setSelectedDependencyRevisionLocator: (locator: string) => void;
  setDependencyToRemove: (depedency: DependencyToRemove | null) => void;
  setDependencyLocatorToOverwrite: (locator: string | null) => void;
  setDependencyLocatorToEdit: (locator: string | null) => void;
  setManualDependencyAddMode: (mode: ManuallyAddedDependencyRequestKind | null) => void;
  setInProgressBuildId: (buildId: number | null) => void;
};

const DependenciesTable: VoidFunctionComponent<Props> = ({
  modals,
  setSelectedDependencyRevisionLocator,
  setDependencyToRemove,
  setDependencyLocatorToOverwrite,
  setDependencyLocatorToEdit,
  setManualDependencyAddMode,
  setInProgressBuildId,
}) => {
  const isLoading = useSelector(DependenciesV2Store.selectors.getAreDependenciesLoading);
  const totalDependencyCount = useSelector(DependenciesV2Store.selectors.getTotalDependencies);

  const page = useSelector(DependenciesV2Store.selectors.getPage);
  const count = useSelector(DependenciesV2Store.selectors.getCount);
  const totalItems = useSelector(DependenciesV2Store.selectors.getTotalDependencies);

  const setPage = useFetchDependencyAction(DependenciesV2Store.actions.setPage);

  return (
    <div className={styles.DependenciesTable}>
      <List>
        <List.Header>
          <div className={styles.DependenciesTableHeaderTitle}>
            <span>Dependencies</span>
            <Badge shape={BadgeShapes.Circle} color={BadgeColors.LightGray}>
              {!isLoading ? totalDependencyCount : '...'}
            </Badge>
            {!isLoading && <PartialResultRefreshButton />}
          </div>
          <div className={styles.DependenciesTableHeaderPagination}>
            <PaginationRangeWithControl page={page} count={count} totalItems={totalItems} setPage={setPage} />
          </div>
        </List.Header>
        <List.Body>
          <DependencyTableContents
            modals={modals}
            setSelectedDependencyRevisionLocator={setSelectedDependencyRevisionLocator}
            setDependencyToRemove={setDependencyToRemove}
            setDependencyLocatorToOverwrite={setDependencyLocatorToOverwrite}
            setDependencyLocatorToEdit={setDependencyLocatorToEdit}
            setManualDependencyAddMode={setManualDependencyAddMode}
            setInProgressBuildId={setInProgressBuildId}
          />
        </List.Body>
      </List>
    </div>
  );
};

export default DependenciesTable;
